import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RideMap } from "@/components/RideMap";
import { StarRating } from "@/components/StarRating";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { PromptPayDialog } from "@/components/PromptPayDialog";
import { LiveRideTrack } from "@/components/LiveRideTrack";
import { ArrowRight, Clock, Calendar, Users, Car, Music, PawPrint, Cigarette, MessageCircle, BadgeCheck, Zap, Loader2, MapPin } from "lucide-react";

const PrefRow = ({ ok, yes, no, icon: Icon }) => (
  <div className={`flex items-center gap-2 text-sm ${ok ? "text-secondary-cosmic" : "text-white/35"}`}>
    <Icon size={16} className={ok ? "text-[#22D3EE]" : "text-white/30"} /> {ok ? yes : no}
  </div>
);

const RideDetail = () => {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seats, setSeats] = useState("1");
  const [booking, setBooking] = useState(false);
  const [contacting, setContacting] = useState(false);
  const [payBooking, setPayBooking] = useState(null);
  const [liveTrack, setLiveTrack] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/rides/${rideId}`).then((r) => setRide(r.data)).catch(() => setRide(null)).finally(() => setLoading(false));
  };
  useEffect(load, [rideId]);

  const handleBook = async () => {
    if (!user) { navigate("/auth", { state: { from: `/rides/${rideId}` } }); return; }
    setBooking(true);
    try {
      const res = await api.post("/bookings", { ride_id: rideId, seats: parseInt(seats) });
      setPayBooking(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setBooking(false); }
  };

  const handleContact = async () => {
    if (!user) { navigate("/auth", { state: { from: `/rides/${rideId}` } }); return; }
    setContacting(true);
    try {
      const res = await api.post("/conversations", { recipient_id: ride.driver.user_id, ride_id: rideId, text: `Hi ${ride.driver.name}, is your ride from ${ride.from_city} to ${ride.to_city} still available?` });
      toast.success("Message sent to driver");
      navigate(`/messages?c=${res.data.conversation_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start conversation");
    } finally { setContacting(false); }
  };

  if (loading) return <div className="max-w-6xl mx-auto px-4 py-10"><Skeleton className="h-80 w-full bg-white/5 rounded-[var(--radius)]" /></div>;
  if (!ride) return <div className="max-w-6xl mx-auto px-4 py-20 text-center"><h2 className="font-display text-2xl">Ride not found</h2><Link to="/search" className="text-[#22D3EE] mt-3 inline-block">Back to search</Link></div>;

  const driver = ride.driver || {};
  const prefs = ride.preferences || {};
  const isOwn = user && driver.user_id === user.user_id;
  const soldOut = ride.seats_available < 1;
  const cancelled = ride.status === "cancelled";
  const routes = ride.from_coords && ride.to_coords ? [{ from: ride.from_coords, to: ride.to_coords, fromLabel: ride.from_city, toLabel: ride.to_city, color: "#22D3EE" }] : [];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <Link to="/search" className="text-sm text-[#22D3EE]">← Back to search</Link>
      <div className="grid lg:grid-cols-3 gap-6 mt-4">
        <div className="lg:col-span-2 space-y-6">
          <Card className="glass p-6">
            <div className="flex items-center gap-3 text-secondary-cosmic">
              <span className="flex items-center gap-1"><Calendar size={15} className="text-[#22D3EE]" /><span className="font-mono-cosmic text-sm">{ride.date}</span></span>
              <span className="flex items-center gap-1"><Clock size={15} className="text-[#22D3EE]" /><span className="font-mono-cosmic text-sm">{ride.time}</span></span>
              {ride.instant_booking && <Badge className="bg-[rgba(247,201,72,0.14)] text-[#FFF7D6] border border-[rgba(247,201,72,0.25)] gap-1"><Zap size={11} /> Instant booking</Badge>}
              {cancelled && <Badge className="bg-[rgba(251,113,133,0.15)] text-[#FB7185] border border-[rgba(251,113,133,0.3)]">Cancelled</Badge>}
            </div>
            <div className="mt-3 flex items-center gap-3 font-display text-2xl sm:text-3xl">
              <MapPin size={20} className="text-[#7C3AED]" /><span>{ride.from_city}</span>
              <ArrowRight size={20} className="text-[#7C3AED]" /><span>{ride.to_city}</span>
            </div>
            <div className="mt-4">
              {liveTrack && ride.from_coords && ride.to_coords ? (
                <LiveRideTrack from={ride.from_coords} to={ride.to_coords} height={280} />
              ) : (
                <RideMap routes={routes} height={280} />
              )}
              {ride.from_coords && ride.to_coords && (
                <Button data-testid="ride-live-track-button" variant="outline" onClick={() => setLiveTrack((v) => !v)} className="mt-3 bg-white/5 border-white/10 text-white hover:bg-white/8 gap-2">
                  <Car size={15} className="text-[#22D3EE]" /> {liveTrack ? "Show route map" : "Track driver (live)"}
                </Button>
              )}
            </div>
            {ride.description && <p className="mt-4 text-secondary-cosmic">{ride.description}</p>}
          </Card>

          <Card className="glass p-6">
            <h3 className="font-display text-lg mb-4">Ride preferences</h3>
            <div className="grid grid-cols-2 gap-3">
              <PrefRow ok={prefs.music} yes="Music on board" no="No music" icon={Music} />
              <PrefRow ok={prefs.pets} yes="Pets welcome" no="No pets" icon={PawPrint} />
              <PrefRow ok={!prefs.smoking} yes="Non-smoking" no="Smoking allowed" icon={Cigarette} />
              <PrefRow ok={prefs.chatty === "love"} yes="Chatty & fun" no={prefs.chatty === "quiet" ? "Quiet ride" : "Depends on mood"} icon={MessageCircle} />
            </div>
            {ride.car && <div className="mt-4 flex items-center gap-2 text-secondary-cosmic"><Car size={16} className="text-[#22D3EE]" /> {ride.car}</div>}
          </Card>

          <Card className="glass p-6" data-testid="ride-detail-driver-card">
            <h3 className="font-display text-lg mb-4">Your captain</h3>
            <Link to={`/profile/${driver.user_id}`} className="flex items-center gap-4 group">
              <Avatar className="h-14 w-14 ring-2 ring-[rgba(34,211,238,0.35)]">
                <AvatarImage src={driver.picture} alt={driver.name} />
                <AvatarFallback className="bg-[#3B82F6] text-white">{(driver.name || "?").slice(0,2)}</AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-1"><span className="font-display text-lg group-hover:text-[#22D3EE] transition-colors">{driver.name}</span>{driver.verified_email && <BadgeCheck size={16} className="text-[#22D3EE]" />}</div>
                <StarRating value={driver.rating || 0} count={driver.review_count} />
                {driver.bio && <p className="text-sm text-muted-cosmic mt-1 max-w-md">{driver.bio}</p>}
              </div>
            </Link>
            {!isOwn && (
              <Button data-testid="ride-detail-contact-button" onClick={handleContact} disabled={contacting} variant="outline" className="mt-4 bg-white/5 border-white/10 text-white hover:bg-white/8 gap-2">
                {contacting ? <Loader2 className="animate-spin" size={16} /> : <MessageCircle size={16} />} Contact {driver.name?.split(" ")[0]}
              </Button>
            )}
          </Card>
        </div>

        {/* Booking card */}
        <div>
          <Card className="glass p-6 sticky top-20">
            <div className="flex items-baseline justify-between">
              <span className="text-secondary-cosmic">Price per seat</span>
              <span data-testid="ride-detail-price-text" className="font-display text-3xl font-semibold text-white">{ride.price_per_seat} <span className="text-base text-muted-cosmic">฿</span></span>
            </div>
            <Separator className="my-4 bg-white/10" />
            <div className="flex items-center justify-between mb-3">
              <span className="flex items-center gap-1 text-secondary-cosmic"><Users size={16} className="text-[#22D3EE]" /> Seats</span>
              <Badge className="bg-[rgba(34,211,238,0.12)] text-[#A5F3FC] border border-[rgba(34,211,238,0.22)]">{ride.seats_available} available</Badge>
            </div>
            {!isOwn && !soldOut && !cancelled && (
              <Select value={seats} onValueChange={setSeats}>
                <SelectTrigger data-testid="ride-detail-seats-select" className="h-11 bg-white/5 border-white/10 text-white mb-3"><SelectValue /></SelectTrigger>
                <SelectContent className="glass-solid text-white">
                  {Array.from({ length: Math.min(4, ride.seats_available) }).map((_, i) => <SelectItem key={i} value={String(i + 1)}>{i + 1} seat{i > 0 ? "s" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="flex items-center justify-between mb-4">
              <span className="text-secondary-cosmic">Total</span>
              <span className="font-display text-xl text-[#FFF7D6]">{(ride.price_per_seat * parseInt(seats || 1)).toFixed(0)} ฿</span>
            </div>
            {isOwn ? (
              <div className="text-center text-sm text-muted-cosmic py-2">This is your ride. Manage it in your <Link to="/dashboard" className="text-[#22D3EE]">dashboard</Link>.</div>
            ) : cancelled ? (
              <Button disabled className="w-full h-12 bg-white/10 text-white/50">Ride cancelled</Button>
            ) : soldOut ? (
              <Button disabled className="w-full h-12 bg-white/10 text-white/50">Sold out</Button>
            ) : (
              <Button data-testid="ride-detail-book-seat-button" onClick={handleBook} disabled={booking} className="w-full h-12 bg-[hsl(var(--primary))] text-white glow-violet hover:glow-cyan gap-2 text-base">
                {booking ? <Loader2 className="animate-spin" size={18} /> : <Zap size={18} />} Book seat
              </Button>
            )}
            <p className="text-xs text-muted-cosmic text-center mt-3">Pay securely with PromptPay after booking.</p>
          </Card>
        </div>
      </div>
      <PromptPayDialog booking={payBooking} type="ride" open={!!payBooking} onOpenChange={(o) => { if (!o) setPayBooking(null); }} onConfirmed={() => { load(); setTimeout(() => navigate("/dashboard"), 700); }} />
    </div>
  );
};

export default RideDetail;
